import { Loader2 } from "lucide-react";
import { TrimExtendPanel, type PreviewSeek } from "../TrimExtendPanel";
import { isTerminalStatus, type JobView } from "./jobView";

/**
 * "Start & end" in the Studio's Edit bar (spec 2026-09-16 §2.4): the Cut &
 * Extend panel (start here / end here, nudges, the speaker row, Smart
 * Extend) driven by the Studio's Player through the `seek` adapter instead
 * of the page's `<video>` ref. Every action keeps its server route; the
 * Studio follows the job it starts.
 */
export function CutExtendGroup({
  view,
  seek,
  onJobStarted,
}: {
  view: JobView;
  seek: PreviewSeek;
  onJobStarted?: () => void;
}): React.JSX.Element {
  if (!view.hasVideo) {
    return <Note>There is no video to trim yet — build one first.</Note>;
  }
  // Hide the trim panel while a re-render is in flight — its deltas would be
  // relative to an mp4 that's about to be replaced.
  if (!isTerminalStatus(view.status)) {
    return (
      <Note>
        <Loader2 size={13} className="ql-spin" style={{ marginRight: 8, verticalAlign: -2 }} />
        A new version is being built — start &amp; end unlock when it lands.
      </Note>
    );
  }
  return (
    <TrimExtendPanel
      view={{ id: view.id, durationSec: view.durationSec }}
      seek={seek}
      currentSpeaker={view.speaker}
      input={view.input}
      // Upload jobs (no input.from) have no re-cuttable source: the panel
      // degrades to the speaker row alone.
      trimmable={Boolean(view.input?.from)}
      onJobStarted={onJobStarted}
    />
  );
}

function Note({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ fontSize: 12.5, color: "var(--ql-fg-muted)", lineHeight: 1.55, padding: "6px 2px" }}>
      {children}
    </div>
  );
}
